import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { err, ok, type Result } from '@shared/result';
import type { PersonId, RequestId } from '@shared/brand';
import type {
  CancelRequestInput,
  ResolveOverdueInput,
  ResolveOverdueResult,
  ReopenRequestInput,
  AssignVacationInput,
  AssignVacationResult,
  DecideRequestInput,
  ReplacementAnswerInput,
  ILogger,
  IVacationRequestRepository,
  Page,
  PageRequest,
  RepositoryError,
  RequestScope,
  SubmitRequestInput,
  SubmitRequestOutput,
} from '@application/ports';
import type {
  RequestApproval,
  RequestWorkItem,
  VacationRequest,
} from '@domain/entities/VacationRequest';
import { COLLECTIONS, firebase } from '../firebase/client';
import {
  decodeRequestApproval,
  decodeRequestWorkItem,
  decodeVacationRequest,
} from '../codecs/entities';
import {
  collectAllPages,
  runCountQuery,
  runPagedQuery,
  translateCallableError,
  translateFirestoreError,
} from './support';

/**
 * The vacation request adapter.
 *
 * Reads are direct and always scoped; writes are callables without exception.
 * Nothing here decides anything: folio allocation, the business-day count, the
 * concurrency cap and the double sign-off rule are all server-authoritative,
 * because every one of them is a value a client would otherwise be able to
 * choose for itself.
 *
 * The query shapes below correspond one-to-one with the composite indexes in
 * `firestore.indexes.json`. A shape without an index fails loudly in development
 * with a console link to create it, which is the behaviour we want — the
 * alternative is a query that quietly degrades into a collection scan.
 */

/**
 * Builds the constraints for a scope.
 *
 * The `touching` window is the interesting one. Firestore permits a range filter
 * on only a single field, so "absences overlapping August" cannot be expressed as
 * `startDate <= end AND endDate >= start`. It is issued as `endDate >= start`
 * — an absence ending before the window began cannot overlap it — and the upper
 * bound on `startDate` is applied in memory over the returned page. That is
 * correct as long as the page covers the window, which is why the callers that use
 * `touching` request a page sized for a sub area rather than a default page.
 */
const constraintsFor = (scope: RequestScope): QueryConstraint[] => {
  const constraints: QueryConstraint[] = [];

  if (scope.personId !== undefined) constraints.push(where('personId', '==', scope.personId));
  if (scope.replacementPersonId !== undefined) {
    constraints.push(where('replacementPersonId', '==', scope.replacementPersonId));
  }
  /*
   * The area travels even when a narrower scope is given, and that is a security
   * requirement rather than a redundant filter.
   *
   * The `list` rule is `inScope(resource.data.areaId)`, and a rule is only
   * provable from the query's own constraints — Firestore will not run a query it
   * cannot show is confined to what the rule allows. A sub-area-scoped query with
   * no `areaId` filter is exactly that: it was refused outright, which is why a
   * leader's approval board came up on an error while an administrator's, scoped
   * by area, worked. The narrower filter still does the work; the area is what
   * makes it legal.
   *
   * `personId` and `replacementPersonId` are the exceptions: each has its own
   * branch in the rule, so they prove themselves and need no area beside them —
   * and adding one would demand a composite index for nothing.
   */
  const provesItself = scope.personId !== undefined || scope.replacementPersonId !== undefined;
  if (!provesItself && scope.areaId !== undefined) {
    constraints.push(where('areaId', '==', scope.areaId));
  }
  if (scope.subAreaId !== undefined) constraints.push(where('subAreaId', '==', scope.subAreaId));

  if (scope.statuses !== undefined && scope.statuses.length > 0) {
    // `in` caps at 30; the status vocabulary is far smaller, so one query suffices.
    constraints.push(where('status', 'in', [...scope.statuses]));
  }

  if (scope.touching !== undefined) {
    constraints.push(where('endDate', '>=', scope.touching.start.toISO()));
    constraints.push(orderBy('endDate'));
  } else {
    constraints.push(orderBy('createdAt', 'desc'));
  }

  return constraints;
};

/** Applies the half of the overlap test Firestore could not express. */
const filterTouching = (
  items: readonly VacationRequest[],
  scope: RequestScope,
): readonly VacationRequest[] => {
  const window = scope.touching;
  if (window === undefined) return items;
  return items.filter((request) => request.range.overlaps(window));
};

/*
 * The live subscription's ceiling. One person's own requests over a career are
 * dozens, not thousands, and a listener without a bound is a promise to stream
 * whatever the collection grows into.
 */
const LIVE_LIMIT = 50;

export class FirestoreVacationRequestRepository implements IVacationRequestRepository {
  constructor(private readonly logger: ILogger) {}

  private get collectionRef() {
    return collection(firebase().db, COLLECTIONS.vacationRequests);
  }

  async getById(requestId: RequestId): Promise<Result<VacationRequest, RepositoryError>> {
    const { doc, getDoc } = await import('firebase/firestore');
    try {
      const snapshot = await getDoc(doc(firebase().db, COLLECTIONS.vacationRequests, requestId));
      if (!snapshot.exists()) return err({ kind: 'not-found', what: `request ${requestId}` });

      const decoded = decodeVacationRequest(snapshot.id, snapshot.data());
      return decoded === null
        ? err({
            kind: 'malformed-document',
            path: `vacationRequests/${requestId}`,
            detail: 'missing or invalid date range',
          })
        : ok(decoded);
    } catch (cause) {
      return err(translateFirestoreError(cause, `request ${requestId}`));
    }
  }

  async getByFolio(folio: string): Promise<Result<VacationRequest, RepositoryError>> {
    const page = await runPagedQuery(
      this.collectionRef,
      [where('folio', '==', folio)],
      { limit: 1 },
      decodeVacationRequest,
      'request by folio',
    );
    if (!page.ok) return err(page.error);

    const found = page.value.items[0];
    return found === undefined ? err({ kind: 'not-found', what: `folio ${folio}` }) : ok(found);
  }

  async list(
    scope: RequestScope,
    page: PageRequest,
  ): Promise<Result<Page<VacationRequest>, RepositoryError>> {
    const result = await runPagedQuery(
      this.collectionRef,
      constraintsFor(scope),
      page,
      decodeVacationRequest,
      'vacationRequests',
      (failures) => this.logger.warn('undecodable vacationRequest documents', { failures }),
    );
    if (!result.ok) return result;

    return ok({
      items: filterTouching(result.value.items, scope),
      nextCursor: result.value.nextCursor,
    });
  }

  /**
   * Live view of one person's own requests.
   *
   * Filtered by `personId` alone, which the rules prove through their `isSelf`
   * branch and which keeps the listener to the handful of documents that belong
   * to the viewer. An undecodable document is skipped rather than collapsing the
   * batch: the same rule the paged reads follow, because one corrupt row must
   * not stop the other nineteen from updating on screen.
   */
  subscribeToPerson(
    personId: PersonId,
    onChange: (requests: readonly VacationRequest[]) => void,
    onError: (error: RepositoryError) => void,
  ): () => void {
    return onSnapshot(
      query(
        this.collectionRef,
        where('personId', '==', personId),
        orderBy('createdAt', 'desc'),
        limit(LIVE_LIMIT),
      ),
      (snapshot) => {
        const items: VacationRequest[] = [];
        for (const document of snapshot.docs) {
          const decoded = decodeVacationRequest(document.id, document.data());
          if (decoded === null) {
            this.logger.warn('undecodable vacationRequest in live subscription', {
              id: document.id,
            });
            continue;
          }
          items.push(decoded);
        }
        onChange(items);
      },
      (cause) => {
        onError(translateFirestoreError(cause, 'live requests'));
      },
    );
  }

  /**
   * Live view of a decision queue.
   *
   * Uses the very same `constraintsFor` the paged read uses, so the shape the
   * rules must prove — and the composite index it needs — are identical to the
   * board's own query. A listener with its own hand-rolled constraints would be
   * a second query shape to keep provable, and the one that fails is always the
   * one nobody tested.
   */
  subscribeToScope(
    scope: RequestScope,
    onChange: (requests: readonly VacationRequest[]) => void,
    onError: (error: RepositoryError) => void,
  ): () => void {
    return onSnapshot(
      query(this.collectionRef, ...constraintsFor(scope), limit(LIVE_LIMIT)),
      (snapshot) => {
        const items: VacationRequest[] = [];
        for (const document of snapshot.docs) {
          const decoded = decodeVacationRequest(document.id, document.data());
          if (decoded === null) {
            this.logger.warn('undecodable vacationRequest in live queue', { id: document.id });
            continue;
          }
          items.push(decoded);
        }
        onChange(filterTouching(items, scope));
      },
      (cause) => {
        onError(translateFirestoreError(cause, 'live queue'));
      },
    );
  }

  /**
   * How many requests match a scope.
   *
   * Server-side aggregation by default: counting a KPI tile by downloading the
   * documents is the most expensive mistake available at this scale.
   *
   * **A `touching` window is the exception, and it has to be.** Firestore permits
   * a range filter on one field only, so the overlap test is split in two —
   * `endDate >= inicio` goes to the server and `startDate <= fin` is applied in
   * memory by `filterTouching`. An aggregate returns a number, so there is nothing
   * left to apply the second half to: for four months this counted every absence
   * ending today *or later*, which put every future holiday of the area inside
   * «Fuera hoy» and read 7 on a day when two people were out. Exact and false, and
   * indistinguishable from correct — nobody can look at a 7 and see the bug.
   *
   * So the window is counted by reading the page and applying the same overlap the
   * list does. It costs documents instead of an aggregate, and it is bounded the
   * same way every `touching` caller is already bounded: by area, by occupying
   * status, and by an end date no earlier than the window's start. What it buys is
   * that the number means what its label says.
   */
  async count(scope: RequestScope): Promise<Result<number, RepositoryError>> {
    if (scope.touching === undefined) {
      return runCountQuery(this.collectionRef, constraintsFor(scope), 'vacationRequests count');
    }

    const matching = await collectAllPages((page) =>
      runPagedQuery(
        this.collectionRef,
        constraintsFor(scope),
        page,
        decodeVacationRequest,
        'vacationRequests count (ventana)',
        (failures) => this.logger.warn('undecodable vacationRequest while counting', { failures }),
      ),
    );
    if (!matching.ok) return err(matching.error);

    return ok(filterTouching(matching.value, scope).length);
  }

  approvalsFor(requestId: RequestId): Promise<Result<readonly RequestApproval[], RepositoryError>> {
    return collectAllPages((page) =>
      runPagedQuery(
        collection(firebase().db, COLLECTIONS.requestApprovals),
        [where('requestId', '==', requestId), orderBy('order')],
        page,
        decodeRequestApproval,
        'requestApprovals',
      ),
    );
  }

  workItemsFor(requestId: RequestId): Promise<Result<readonly RequestWorkItem[], RepositoryError>> {
    return collectAllPages((page) =>
      runPagedQuery(
        collection(firebase().db, COLLECTIONS.requestWorkItems),
        [where('requestId', '==', requestId)],
        page,
        decodeRequestWorkItem,
        'requestWorkItems',
      ),
    );
  }

  // -------------------------------------------------------------------------
  // Mutations — callables only.
  // -------------------------------------------------------------------------

  /**
   * Submits a request. The payload carries the *intent*; the server decides the
   * folio, the business-day count and how many signatures are needed, and returns
   * them so the confirmation screen shows what was actually recorded rather than
   * what the client guessed.
   */
  async submit(input: SubmitRequestInput): Promise<Result<SubmitRequestOutput, RepositoryError>> {
    const payload = {
      startDate: input.range.start.toISO(),
      endDate: input.range.end.toISO(),
      replacementPersonId: input.replacementPersonId,
      notes: input.notes,
      workItems: input.workItems,
      rescheduledFromFolio: input.rescheduledFromFolio,
    };

    return this.call<typeof payload, SubmitRequestOutput>('submitVacationRequest', payload);
  }

  assign(input: AssignVacationInput): Promise<Result<AssignVacationResult, RepositoryError>> {
    return this.call<AssignVacationInput, AssignVacationResult>('assignVacation', input);
  }

  decide(input: DecideRequestInput): Promise<Result<void, RepositoryError>> {
    const payload = {
      requestId: input.requestId,
      decision: input.decision,
      reason: input.reason,
      suggestedRanges: input.suggestedRanges.map((r) => r.toISO()),
      waiveReplacementReason: input.waiveReplacementReason,
    };
    return this.call<typeof payload, void>('decideVacationRequest', payload);
  }

  respondToReplacement(input: ReplacementAnswerInput): Promise<Result<void, RepositoryError>> {
    const payload = {
      requestId: input.requestId,
      answer: input.answer,
      reason: input.reason,
    };
    return this.call<typeof payload, void>('respondToReplacement', payload);
  }

  changeReplacement(
    requestId: RequestId,
    replacementPersonId: PersonId,
  ): Promise<Result<void, RepositoryError>> {
    const payload = { requestId, replacementPersonId };
    return this.call<typeof payload, void>('changeReplacement', payload);
  }

  cancel(input: CancelRequestInput): Promise<Result<void, RepositoryError>> {
    return this.call<CancelRequestInput, void>('cancelVacationRequest', input);
  }

  resolveOverdue(
    input: ResolveOverdueInput,
  ): Promise<Result<ResolveOverdueResult, RepositoryError>> {
    const payload = {
      requestId: input.requestId,
      outcome: input.outcome,
      reason: input.reason,
      suggestReschedule: input.suggestReschedule ?? false,
    };
    return this.call<typeof payload, ResolveOverdueResult>('resolveOverdueRequest', payload);
  }

  /**
   * Borra un registro por cuenta ajena. Sin motivo del catálogo, a diferencia de
   * cancelar: no hay hueco que explicarle a nadie porque el registro se va entero.
   */
  deleteAssigned(requestId: RequestId): Promise<Result<void, RepositoryError>> {
    return this.call<{ requestId: string }, void>('deleteAssignedVacation', { requestId });
  }

  /**
   * The kanban drag between "Por revisar" and "En revisión".
   *
   * A presentational transition with no business consequence, which is why it has
   * its own narrow callable instead of going through `decide` — dragging a card
   * must never be able to approve anything.
   */
  moveToColumn(
    requestId: RequestId,
    status: VacationRequest['status'],
  ): Promise<Result<void, RepositoryError>> {
    return this.call<{ requestId: string; status: string }, void>('moveRequestColumn', {
      requestId,
      status,
    });
  }

  reopen(input: ReopenRequestInput): Promise<Result<void, RepositoryError>> {
    return this.call<{ requestId: string; status: string; reason: string }, void>(
      'reopenVacationRequest',
      { requestId: input.requestId, status: input.status, reason: input.reason },
    );
  }


  private async call<Req, Res>(name: string, payload: Req): Promise<Result<Res, RepositoryError>> {
    try {
      const { functions } = firebase();
      const invoke = httpsCallable<Req, Res>(functions, name);
      const response = await invoke(payload);
      return ok(response.data);
    } catch (cause) {
      this.logger.error(`callable ${name} failed`, { cause: String(cause) });
      return err(translateCallableError(cause, name));
    }
  }
}
